import { useCallback, useEffect, useId, useRef, useState, type CSSProperties } from 'react'
import { theme } from 'antd'
import { useTranslation } from 'react-i18next'

/**
 * Uzun yordamchi matn — yopiq holatda IKKI QATOR, keyin «Batafsil».
 *
 * <h2>Nega bu komponent kerak bo'ldi</h2>
 * Tashqi tekshiruvchi hisoboti (XSB-004): «bir nechta maydon ostida uzun
 * va texnik izohlar ko'rsatiladi — bu forma skan qilinishini qiyinlashtiradi
 * va asosiy ma'lumotlarni vizual jihatdan bosib ketadi».
 *
 * ⚠️ MATN QISQARTIRILMAYDI, YASHIRILADI. Bu farq muhim: QARORLAR.md §23.2,
 * 6-qoida uzun izohni qisqartirishni ATAYLAB taqiqlaydi — u «nega shunday»
 * va «nima yo'qoladi» ni yozadi, ya'ni faktni olib tashlash ma'lumotni yo'q
 * qilish bo'lardi. Bu yerda matnning HAMMASI joyida qoladi, faqat birinchi
 * qarashda ikki qatordan ortig'i ko'rinmaydi.
 *
 * <h2>⚠️ TUGMA QISQA MATNDA UMUMAN CHIQMAYDI</h2>
 * Aks holda har maydon ostida bosishdan foyda yo'q «Batafsil» paydo bo'lardi
 * — ya'ni nuqsonni tuzatish o'rniga uni ko'paytirgan bo'lardik. Shuning
 * uchun matn haqiqatan sig'maganini O'LCHAYMIZ (`scrollHeight` va
 * `clientHeight`), belgilar sonidan taxmin qilmaymiz: bir xil uzunlikdagi
 * matn 300px va 800px kartada boshqacha sig'adi.
 *
 * ⚠️ O'lchov oyna kengayganda QAYTA olinadi (`ResizeObserver`) — aks holda
 * planshetni aylantirgan operatorda tugma sig'ib turgan matn ostida qolib
 * ketardi.
 *
 * <h2>⚠️ `div onClick` EMAS, `button`</h2>
 * Klaviatura bilan ishlaydigan va skrin-rider bilan o'qiydigan operator
 * uchun: tugma tabulyatsiyada topiladi, `Enter`/`Probel` bilan ishlaydi va
 * `aria-expanded` orqali holatini aytadi. `aria-controls` matn bo'lagiga
 * ishora qiladi, ya'ni «nima ochildi» degan savol ham javobsiz qolmaydi.
 *
 * ⚠️ Kesish `-webkit-line-clamp` bilan: u QATOR CHEGARASIDA to'xtaydi va
 * uch nuqta qo'yadi. `max-height` + `overflow: hidden` esa matnni harflar
 * o'rtasidan kesardi va oxirgi qator yarim ko'ringan holda qolardi.
 */
export function ExpandableHint({
  text,
  lines = 2,
  fontSize = 12,
  style,
}: {
  text: string
  /** Yopiq holatda nechta qator ko'rinadi */
  lines?: number
  fontSize?: number
  style?: CSSProperties
}) {
  const { t } = useTranslation()
  const { token } = theme.useToken()
  const bodyId = useId()
  const bodyRef = useRef<HTMLDivElement>(null)
  const [expanded, setExpanded] = useState(false)
  const [clipped, setClipped] = useState(false)

  const measure = useCallback(() => {
    const el = bodyRef.current
    if (!el) return
    /*
      ⚠️ O'LCHOV FAQAT YOPIQ HOLATDA to'g'ri bo'ladi: ochiq holatda
      `scrollHeight === clientHeight` va tugma o'zini o'zi yo'q qilardi
      («Yig'ish» bosib bo'lmaydigan holatga tushib qolardi).
    */
    if (expanded) return
    setClipped(el.scrollHeight - el.clientHeight > 1)
  }, [expanded])

  useEffect(() => {
    measure()
    const el = bodyRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [measure, text])

  if (!text) return null

  return (
    <div style={style}>
      {/*
        ⚠️ `Typography.Text` EMAS, oddiy `div`. Sabab o'lchovda: bu yerda
        `ref` va `-webkit-box` kerak, `Typography.Text` esa `span` chizadi
        va uning `ref` turi antd ichki turiga bog'langan. Rang baribir
        mavzu tokenidan olinadi, ya'ni ko'rinishi `type="secondary"` bilan
        bir xil.
      */}
      <div
        ref={bodyRef}
        id={bodyId}
        style={{
          fontSize,
          color: token.colorTextSecondary,
          display: '-webkit-box',
          WebkitBoxOrient: 'vertical',
          // Ochiq holatda cheklov olib tashlanadi, `display` esa o'zgarmaydi
          // — matn «sakramasin»
          WebkitLineClamp: expanded ? 'unset' : lines,
          overflow: expanded ? 'visible' : 'hidden',
        }}
      >
        {text}
      </div>

      {clipped ? (
        <button
          type="button"
          aria-expanded={expanded}
          aria-controls={bodyId}
          onClick={() => setExpanded((v) => !v)}
          style={{
            display: 'block',
            marginTop: 2,
            padding: 0,
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            font: 'inherit',
            fontSize,
            color: token.colorLink,
          }}
        >
          {expanded ? t('common.showLess') : t('common.showMore')}
        </button>
      ) : null}
    </div>
  )
}
